import React, { useState } from 'react';
import { Card, CardBody, Input, Checkbox, Button } from '@heroui/react';
import { ArrowLeft, Check } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { AssetCategoryTemplate, EMPTY_TEMPLATE } from 'shared/config/assetTemplates';
import { CategoryTemplateGallery } from './CategoryTemplateGallery';
import { useCreateAssetCategory, useCreateAssetType, useCreateFieldDefinition } from 'hooks/useAssets';

interface InlineQuickCreateProps {
  onSuccess: () => void;
  onCancel: () => void;
}

type Step = 'select-template' | 'configure';

export const InlineQuickCreate: React.FC<InlineQuickCreateProps> = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState<Step>('select-template');
  const [selectedTemplate, setSelectedTemplate] = useState<AssetCategoryTemplate | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const { createCategory } = useCreateAssetCategory();
  const { createAssetType } = useCreateAssetType();
  const { createFieldDefinition } = useCreateFieldDefinition();

  const handleTemplateSelection = (template: AssetCategoryTemplate) => {
    setSelectedTemplate(template);

    if (template.id === 'empty') {
      setCategoryName('');
      setSelectedTypes([]);
    } else {
      setCategoryName(template.name);
      setSelectedTypes(template.types.map((t) => t.name));
    }

    setStep('configure');
  };

  const handleCustomStart = () => {
    setSelectedTemplate(EMPTY_TEMPLATE);
    setCategoryName('');
    setSelectedTypes([]);
    setStep('configure');
  };

  const handleBack = () => {
    setStep('select-template');
    setSelectedTemplate(null);
  };

  const handleToggleType = (typeName: string) => {
    setSelectedTypes((prev) =>
      prev.includes(typeName) ? prev.filter((t) => t !== typeName) : [...prev, typeName]
    );
  };

  const handleCreate = async () => {
    if (!categoryName.trim()) {
      toast.error('Voer een categorienaam in');
      return;
    }

    if (selectedTypes.length === 0) {
      toast.error('Selecteer minimaal één type');
      return;
    }

    try {
      setIsCreating(true);

      // Create category
      const createdCategory = await createCategory({
        name: categoryName,
        description: selectedTemplate?.description,
        displayOrder: selectedTemplate?.displayOrder || 0,
      });

      // Create selected types
      const selectedTypeDetails =
        selectedTemplate?.types.filter((t) => selectedTypes.includes(t.name)) || [];

      for (let i = 0; i < selectedTypeDetails.length; i++) {
        const type = selectedTypeDetails[i];
        await createAssetType({
          assetCategoryId: createdCategory.id,
          name: type.name,
          description: type.description,
          scopes: type.scopes,
          displayOrder: i,
        });
      }

      // Create field definitions
      if (selectedTemplate?.fields) {
        for (let i = 0; i < selectedTemplate.fields.length; i++) {
          const field = selectedTemplate.fields[i];
          await createFieldDefinition({
            assetCategoryId: String(createdCategory.id),
            fieldName: field.fieldName,
            displayName: field.displayName,
            fieldType: field.fieldType,
            displayOrder: i,
            validationRules: field.isRequired ? { required: true } : undefined,
            enumValues: field.enumValues,
            metadata: field.metadata,
          });
        }
      }

      toast.success(`Categorie "${categoryName}" succesvol aangemaakt`);
      onSuccess();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error instanceof Error ? error.message : 'Fout bij aanmaken van categorie');
      setIsCreating(false);
    }
  };

  if (step === 'select-template') {
    return (
      <Card className="border border-gray-200">
        <CardBody className="p-6">
          <CategoryTemplateGallery
            onSelectTemplate={handleTemplateSelection}
            onCustomStart={handleCustomStart}
          />
          <div className="mt-4 flex justify-end">
            <Button variant="light" onPress={onCancel}>
              Annuleren
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Configure step
  return (
    <Card className="border border-gray-200">
      <CardBody className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Categorie configureren</h3>
          <Button
            size="sm"
            variant="light"
            startContent={<ArrowLeft size={16} />}
            onPress={handleBack}
            isDisabled={isCreating}
          >
            Terug
          </Button>
        </div>

        {/* Category Name */}
        <div>
          <Input
            label="Categorienaam"
            placeholder="bijv. Elektrische Toestellen"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            isRequired
            size="md"
          />
        </div>

        {/* Type Selection */}
        {selectedTemplate && selectedTemplate.types.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Selecteer types ({selectedTypes.length} van {selectedTemplate.types.length})
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {selectedTemplate.types.map((type, index) => (
                <Card
                  key={index}
                  isPressable
                  onPress={() => handleToggleType(type.name)}
                  className={`border transition-all ${
                    selectedTypes.includes(type.name)
                      ? 'border-moss-primary bg-moss-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CardBody className="p-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        isSelected={selectedTypes.includes(type.name)}
                        onValueChange={() => handleToggleType(type.name)}
                        color="success"
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{type.name}</p>
                        {type.description && (
                          <p className="text-xs text-gray-600 truncate">{type.description}</p>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        {selectedTemplate?.fields && selectedTemplate.fields.length > 0 && (
          <div className="text-xs text-gray-600 bg-blue-50 rounded p-3">
            <Check size={14} weight="bold" className="inline text-blue-600 mr-1" />
            {selectedTemplate.fields.length} aangepaste velden worden automatisch toegevoegd
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="light" onPress={onCancel} isDisabled={isCreating}>
            Annuleren
          </Button>
          <Button
            color="primary"
            onPress={handleCreate}
            isLoading={isCreating}
            isDisabled={!categoryName.trim() || selectedTypes.length === 0}
            startContent={!isCreating && <Check size={18} weight="bold" />}
          >
            Categorie Aanmaken
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};
